import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Image, Camera, FileUp, X, MapPin, Loader2, Locate, Ruler, Landmark, Pin, PinOff } from "lucide-react";

const UNITS = ["sq ft", "sq m", "ft", "m", "cm", "inch", "yard", "acre", "cent", "gaz"];

const empty = {
  title: "", description: "", property_type: "house", listing_type: "sale",
  price: "", price_unit: "", pincode: "", area_name: "", address: "", city: "",
  lat: null, lon: null, bedrooms: "", bathrooms: "", area_sqft: "", dimensions: "",
  facing: "", detailed_dimensions: [], rera_license: "", video_url: "", photos: [], documents: [],
  amenities: [],
  lister_name: "", lister_phone: "", lister_email: "",
  pinned_loan_providers: [],
};

const FACINGS = ["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"];
const AMENITIES = ["Hall / Living Room", "Modular Kitchen", "Master Bedroom", "Balcony", "Car Parking", "Furnished", "Lift", "Power Backup", "Water Supply 24x7", "Garden", "Security", "Swimming Pool", "Gym", "Pooja Room", "Servant Room"];

export default function PropertyForm() {
  const { id } = useParams();
  const editing = !!id;
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const isBuilt = !["land", "commercial"].includes(form.property_type);
  const isStaff = user && (user.role === "admin" || user.role === "subadmin");
  const [loaded, setLoaded] = useState(!id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [loanProvs, setLoanProvs] = useState([]);
  const galleryRef = useRef(); const cameraRef = useRef(); const docRef = useRef();
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const parseArea = (s) => {
    const m = (s || "").match(/(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)/);
    return m ? Math.round(parseFloat(m[1]) * parseFloat(m[2])) : null;
  };
  const updDimensions = (v) => setForm((f) => { const a = parseArea(v); return { ...f, dimensions: v, ...(a != null ? { area_sqft: a } : {}) }; });

  const addDim = () => setForm((f) => ({ ...f, detailed_dimensions: [...f.detailed_dimensions, { label: "", value: "", unit: "sq ft" }] }));
  const updateDim = (i, k, v) => setForm((f) => ({ ...f, detailed_dimensions: f.detailed_dimensions.map((d, x) => x === i ? { ...d, [k]: v } : d) }));
  const removeDim = (i) => setForm((f) => ({ ...f, detailed_dimensions: f.detailed_dimensions.filter((_, x) => x !== i) }));

  useEffect(() => {
    if (editing) api.get(`/properties/${id}`).then((r) => {
      const d = r.data;
      setForm({ ...empty, ...d, price: d.price ?? "", bedrooms: d.bedrooms ?? "", bathrooms: d.bathrooms ?? "", area_sqft: d.area_sqft ?? "" });
      setLoaded(true);
    });
  }, [id, editing]);

  useEffect(() => { api.get("/loan-providers").then((r) => setLoanProvs(r.data.providers || [])).catch(() => {}); }, []);
  const togglePin = (pid) => setForm((f) => ({ ...f, pinned_loan_providers: (f.pinned_loan_providers || []).includes(pid) ? f.pinned_loan_providers.filter((x) => x !== pid) : [...(f.pinned_loan_providers || []), pid] }));

  const uploadFiles = async (files, kind) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data);
      }
      if (kind === "photo") upd("photos", [...form.photos, ...uploaded.map((u) => u.url)]);
      else upd("documents", [...form.documents, ...uploaded.map((u) => ({ url: u.url, name: u.name }))]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const fetchFromText = async () => {
    const q = `${form.area_name} ${form.pincode} ${form.city}`.trim();
    if (!q) { toast.error("Enter pincode or area first"); return; }
    setGeoLoading(true);
    try {
      const { data } = await api.get("/geocode", { params: { q } });
      if (data.results.length) {
        const r = data.results[0];
        upd("lat", r.lat); upd("lon", r.lon);
        if (!form.pincode && r.pincode) upd("pincode", r.pincode);
        toast.success("Location fetched on map");
      } else toast.error("Location not found");
    } finally { setGeoLoading(false); }
  };

  const useGps = () => {
    if (!navigator.geolocation) { toast.error("Not supported"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const lat = pos.coords.latitude, lon = pos.coords.longitude;
      upd("lat", lat); upd("lon", lon);
      const acc = Math.round(pos.coords.accuracy || 0);
      const { data } = await api.get("/reverse", { params: { lat, lon } });
      if (data.area_name) upd("area_name", data.area_name);
      if (data.pincode) upd("pincode", data.pincode);
      if (data.city) upd("city", data.city);
      if (data.display_name) upd("address", data.display_name);
      setGeoLoading(false);
      toast.success(acc ? `Current location applied (±${acc}m)` : "Current location applied");
    }, () => { setGeoLoading(false); toast.error("Could not get location — please allow location access"); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };

  const save = async () => {
    if (!form.title || !form.price) { toast.error("Title and price are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        bedrooms: form.bedrooms === "" ? null : parseInt(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : parseInt(form.bathrooms),
        area_sqft: form.area_sqft === "" ? null : parseFloat(form.area_sqft),
        pinned_loan_providers: form.pinned_loan_providers || [],
      };
      let res;
      if (editing) res = await api.put(`/properties/${id}`, payload);
      else res = await api.post("/properties", payload);
      if (res.data && res.data.pending_approval) {
        toast.success(res.data.message || "Sent to admin for approval");
      } else {
        toast.success("Property saved");
      }
      navigate(user && (user.role === "admin" || user.role === "subadmin") ? "/admin" : "/broker");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-heading font-bold text-3xl text-brand-ink tracking-tight mb-6">{editing ? t("form.editProperty") : t("form.newProperty")}</h1>

      {!loaded ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-emerald" /></div>
      ) : (
      <div className="space-y-6">
        {/* Lister contact — staff only */}
        {isStaff && !editing && (
          <Section title="Mediator / Owner Contact">
            <p className="text-xs text-brand-muted -mt-2">Enter the mediator/owner this listing belongs to. If their phone or email matches a registered mediator/owner, the listing is linked to their account automatically.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Name"><Input value={form.lister_name} onChange={(e) => upd("lister_name", e.target.value)} placeholder="Full name" data-testid="f-lister-name" /></Field>
              <Field label="Phone"><Input value={form.lister_phone} onChange={(e) => upd("lister_phone", e.target.value)} placeholder="98765 43210" data-testid="f-lister-phone" /></Field>
              <Field label="Email"><Input value={form.lister_email} onChange={(e) => upd("lister_email", e.target.value)} placeholder="name@email.com" data-testid="f-lister-email" /></Field>
            </div>
          </Section>
        )}

        {/* Basic */}
        <Section title="Basic Info">
          <Field label={t("form.title")}><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="f-title" /></Field>
          <Field label={t("form.desc")}><Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="f-desc" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("form.listingType")}>
              <Select value={form.listing_type} onValueChange={(v) => upd("listing_type", v)}>
                <SelectTrigger data-testid="f-listing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">{t("filter.sale")}</SelectItem>
                  <SelectItem value="rent">{t("filter.rent")}</SelectItem>
                  <SelectItem value="lease">{t("filter.lease")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("form.propType")}>
              <Select value={form.property_type} onValueChange={(v) => upd("property_type", v)}>
                <SelectTrigger data-testid="f-ptype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="villa">Villa</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="land">Land / Plot</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("form.price")}><Input type="number" value={form.price} onChange={(e) => upd("price", e.target.value)} data-testid="f-price" /></Field>
            {form.listing_type !== "sale" && (
              <Field label={t("form.priceUnit")}><Input value={form.price_unit} onChange={(e) => upd("price_unit", e.target.value)} placeholder="e.g. per month" data-testid="f-priceunit" /></Field>
            )}
          </div>
        </Section>

        {/* Location */}
        <Section title={t("prop.location")}>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("form.pincode")}><Input value={form.pincode} onChange={(e) => upd("pincode", e.target.value)} data-testid="f-pincode" /></Field>
            <Field label={t("form.areaName")}><Input value={form.area_name} onChange={(e) => upd("area_name", e.target.value)} data-testid="f-area" /></Field>
          </div>
          <Field label={t("form.city")}><Input value={form.city} onChange={(e) => upd("city", e.target.value)} data-testid="f-city" /></Field>
          <Field label={t("form.address")}><Input value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="f-address" /></Field>
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="outline" className="rounded-full" onClick={fetchFromText} disabled={geoLoading} data-testid="f-geocode">
              {geoLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MapPin className="w-4 h-4 mr-1.5" />} {t("form.fetchLoc")}
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={useGps} disabled={geoLoading} data-testid="f-gps">
              <Locate className="w-4 h-4 mr-1.5" /> {t("form.useGps")}
            </Button>
          </div>
          {form.lat != null && (
            <iframe title="preview" className="w-full h-56 rounded-xl border border-brand-line mt-2"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${form.lon - 0.02}%2C${form.lat - 0.015}%2C${form.lon + 0.02}%2C${form.lat + 0.015}&layer=mapnik&marker=${form.lat}%2C${form.lon}`} />
          )}
        </Section>

        {/* Details */}
        <Section title={t("prop.details")}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {isBuilt && <Field label={t("form.beds")}><Input type="number" value={form.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} data-testid="f-beds" /></Field>}
            {isBuilt && <Field label={t("form.baths")}><Input type="number" value={form.bathrooms} onChange={(e) => upd("bathrooms", e.target.value)} data-testid="f-baths" /></Field>}
            <Field label={t("form.sqft")}><Input type="number" value={form.area_sqft} onChange={(e) => upd("area_sqft", e.target.value)} data-testid="f-sqft" /></Field>
            <Field label={t("form.dimensions")}><Input value={form.dimensions} onChange={(e) => updDimensions(e.target.value)} placeholder="e.g. 30 x 40" data-testid="f-dim" /></Field>
          </div>
          {isBuilt && (
            <div data-testid="f-amenities">
              <div className="text-sm font-medium text-brand-ink mb-2">Facilities & Amenities</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AMENITIES.map((a) => {
                  const on = (form.amenities || []).includes(a);
                  return (
                    <button type="button" key={a} data-testid={`amenity-${a}`} onClick={() => upd("amenities", on ? form.amenities.filter((x) => x !== a) : [...(form.amenities || []), a])}
                      className={`text-left text-sm rounded-lg border px-3 py-2 transition-colors ${on ? "border-brand-emerald bg-brand-emerald/10 text-brand-emerald font-medium" : "border-brand-line text-brand-muted hover:border-brand-emerald/40"}`}>
                      <span className="inline-flex items-center gap-2">{on ? "✓" : "+"} {a}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("form.facing")}>
              <Select value={form.facing} onValueChange={(v) => upd("facing", v)}>
                <SelectTrigger data-testid="f-facing"><SelectValue placeholder={t("form.facingPick")} /></SelectTrigger>
                <SelectContent>
                  {FACINGS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("form.rera")}><Input value={form.rera_license} onChange={(e) => upd("rera_license", e.target.value)} placeholder="Optional" data-testid="f-rera" /></Field>
          </div>
          <Field label="Video Walkthrough URL (optional)"><Input value={form.video_url} onChange={(e) => upd("video_url", e.target.value)} placeholder="YouTube link e.g. https://youtu.be/..." data-testid="f-video" /></Field>
        </Section>

        {/* Detailed dimensions */}
        <Section title={t("dim.title")}>
          <p className="text-xs text-brand-muted -mt-2">{t("dim.hint")}</p>
          {form.detailed_dimensions.map((d, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_100px_120px_40px] gap-2 items-end border-b border-brand-line sm:border-0 pb-3 sm:pb-0" data-testid={`dim-row-${i}`}>
              <div><Label className="text-xs">{t("dim.label")}</Label><Input value={d.label} onChange={(e) => updateDim(i, "label", e.target.value)} placeholder="Hall" className="mt-1" data-testid={`dim-label-${i}`} /></div>
              <div><Label className="text-xs">{t("dim.value")}</Label><Input value={d.value} onChange={(e) => updateDim(i, "value", e.target.value)} placeholder="12 x 15" className="mt-1" data-testid={`dim-value-${i}`} /></div>
              <div>
                <Label className="text-xs">{t("dim.unit")}</Label>
                <Select value={d.unit} onValueChange={(v) => updateDim(i, "unit", v)}>
                  <SelectTrigger className="mt-1" data-testid={`dim-unit-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button type="button" onClick={() => removeDim(i)} className="h-10 w-10 rounded-xl border border-brand-line flex items-center justify-center text-brand-terracotta" data-testid={`dim-remove-${i}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
          <Button type="button" variant="outline" className="rounded-full" onClick={addDim} data-testid="dim-add"><Ruler className="w-4 h-4 mr-1.5" /> {t("dim.add")}</Button>
        </Section>

        {/* Photos */}
        <Section title={t("form.photos")}>
          <div className="flex flex-wrap gap-3">
            <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles([...e.target.files], "photo")} />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => uploadFiles([...e.target.files], "photo")} />
            <Button type="button" variant="outline" className="rounded-full" onClick={() => galleryRef.current.click()} data-testid="f-gallery"><Image className="w-4 h-4 mr-1.5" /> {t("form.gallery")}</Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => cameraRef.current.click()} data-testid="f-camera"><Camera className="w-4 h-4 mr-1.5" /> {t("form.camera")}</Button>
            {uploading && <span className="flex items-center text-sm text-brand-muted"><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {t("form.uploading")}</span>}
          </div>
          {form.photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
              {form.photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-brand-line">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => upd("photos", form.photos.filter((_, x) => x !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Documents */}
        <Section title={t("form.docs")}>
          <input ref={docRef} type="file" accept=".pdf,image/*" multiple hidden onChange={(e) => uploadFiles([...e.target.files], "doc")} />
          <Button type="button" variant="outline" className="rounded-full" onClick={() => docRef.current.click()} data-testid="f-docs"><FileUp className="w-4 h-4 mr-1.5" /> {t("form.docs")}</Button>
          {form.documents.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {form.documents.map((d, i) => (
                <span key={i} className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full text-sm">
                  {d.name} <button onClick={() => upd("documents", form.documents.filter((_, x) => x !== i))}><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          )}
        </Section>

        {loanProvs.length > 0 && (
        <Section title="Loan Options">
          <p className="text-xs text-brand-muted -mt-2">Pin lenders to show in this property's Home Loan Options (sale listings). If none pinned, all active providers show by default.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {loanProvs.map((p) => {
              const pinned = (form.pinned_loan_providers || []).includes(p.id);
              return (
                <button type="button" key={p.id} onClick={() => togglePin(p.id)} data-testid={`pin-loan-${p.id}`}
                  className={`flex items-center justify-between gap-2 border rounded-xl px-3 py-2.5 text-left transition-colors ${pinned ? "border-brand-emerald bg-brand-emerald/5" : "border-brand-line hover:border-brand-emerald"}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    {p.logo ? <img src={fileUrl(p.logo)} alt="" className="w-8 h-8 rounded object-contain bg-white border border-brand-line shrink-0" /> : <Landmark className="w-5 h-5 text-brand-emerald shrink-0" />}
                    <span className="min-w-0"><span className="text-sm font-medium text-brand-ink block truncate">{p.name}</span>{p.interest_rate && <span className="text-xs text-brand-emerald">{p.interest_rate}</span>}</span>
                  </span>
                  {pinned ? <Pin className="w-4 h-4 text-brand-emerald shrink-0" /> : <PinOff className="w-4 h-4 text-brand-muted shrink-0" />}
                </button>
              );
            })}
          </div>
        </Section>
        )}

        <div className="flex gap-3">
          <Button onClick={save} disabled={saving} className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover flex-1" data-testid="save-property">
            {saving ? t("loading") : t("form.save")}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/broker")} data-testid="cancel-btn">{t("form.cancel")}</Button>
        </div>
      </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-card border border-brand-line rounded-2xl p-6 space-y-4">
    <h2 className="font-heading font-semibold text-lg text-brand-ink">{title}</h2>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div><Label className="mb-1.5 block">{label}</Label>{children}</div>
);
